import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";

import { routes } from "./routes";
import { store } from "../store/store";
import { canAccess } from "../../features/auth/utils";
import { fetchProfile, selectIsAuthenticated, selectAuthUser } from "../../features/auth/authSlice";
import { fetchUrgencies } from "../../features/urgencies/urgenciesSlice";
import { fetchTicketPriorities } from "../../features/ticketPriorities/ticketPrioritiesSlice";
import { fetchDaysOfWeek } from "../../features/daysOfWeek/daysOfWeekSlice";
import { fetchUserRoles } from "../../features/userRoles/userRolesSlice";
import { fetchDepartments } from "../../features/departments/departmentsSlice";
import { fetchLocations } from "../../features/locations/locationsSlice";
import LoadingSpinner from "../components/loadingSpinner/LoadingSpinner";
import styles from "./ProtectedRoute.module.css";

interface ProtectedRouteProps {
  requiredPrivilege?: number;
}

export default function ProtectedRoute({ requiredPrivilege }: ProtectedRouteProps) {
  const authUser = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCommon = async () => {
      setIsLoading(true);
      await Promise.all([
        store.dispatch(fetchProfile()),
        store.dispatch(fetchUrgencies()),
        store.dispatch(fetchTicketPriorities()),
        store.dispatch(fetchDaysOfWeek()),
        store.dispatch(fetchDepartments()),
        store.dispatch(fetchUserRoles()),
        store.dispatch(fetchLocations()),
      ]);
      setIsLoading(false);
    };
    if (isAuthenticated) {
      loadCommon();
    }
  }, []);

  if (!isAuthenticated) {
    return <Navigate to={`${routes.login.path}?redirectUrl=${encodeURIComponent(window.location.pathname)}`} />;
  }
  if (!canAccess(requiredPrivilege, authUser?.privilege)) {
    return <Navigate to={routes.unauthorized.path} />;
  }
  return isLoading ? (
    <div className={`${styles.spinnerContainer} flex align-items-center justify-content-center`}>
      <LoadingSpinner />
    </div>
  ) : (
    <Outlet />
  );
}
